import * as React from 'react'

import { Box, Flex, HStack, Progress, Image, Text, VStack } from '@chakra-ui/react'

import type { GameState, DownloadingState } from './types'
import { GameStateType } from './types'
import FRAGFI_LOGO from 'url:./brand/fragfi-logo.png'
import FRAGFI_BANNER from 'url:./brand/fragfi-banner.jpg'
import MACHINEGUN from 'url:./static/machinegun.png'
import ROCKET from 'url:./static/rocket.png'
import RIFLE from 'url:./static/rifle.png'
import SHOTGUN from 'url:./static/shotgun.png'

type Props = {
  state: GameState
}

function getLoadingText(state: GameState) {
  if (state.type === GameStateType.PageLoading) return 'Preparing arena'
  if (state.type === GameStateType.Downloading) return 'Syncing arena assets'
  if (state.type === GameStateType.Running) return 'Dropping into AlphaOps'
  if (state.type === GameStateType.MapChange) return `Loading ${state.map}`
  if (state.type === GameStateType.GameError) return 'Load interrupted'
  return 'Loading AlphaOps'
}

function getProgress(state: GameState) {
  if (state.type === GameStateType.Downloading) {
    return Math.max(2, Math.min(100, (state as DownloadingState).progress * 100))
  }
  if (state.type === GameStateType.MapChange) return 70
  if (state.type === GameStateType.Running) return 92
  return 18
}

export default function StatusOverlay(props: Props) {
  React.useEffect(() => {
    document.getElementById('early-brand-loader')?.remove()
  }, [])

  const { state } = props
  const progress = getProgress(state)

  return (
    <Flex
      align="stretch"
      justify="center"
      minH="100%"
      overflow="hidden"
      position="relative"
      bg="#030303"
      color="white"
      fontFamily="system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
      _before={{
        content: '""',
        position: 'absolute',
        inset: 0,
        backgroundImage: `linear-gradient(90deg, rgba(3,3,3,0.88), rgba(3,3,3,0.58), rgba(3,3,3,0.94)), url(${FRAGFI_BANNER})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        filter: 'contrast(1.15) saturate(0.72)',
        transform: 'scale(1.08)',
      }}
      _after={{
        content: '""',
        position: 'absolute',
        inset: 0,
        background:
          'repeating-linear-gradient(0deg, rgba(255,255,255,0.045) 0px, rgba(255,255,255,0.045) 1px, transparent 1px, transparent 5px), radial-gradient(circle at 18% 78%, rgba(195, 18, 18, 0.45), transparent 28%), linear-gradient(90deg, rgba(145, 14, 14, 0.22), transparent 40%)',
        opacity: 0.8,
      }}
    >
      <Box position="absolute" inset="28px" border="1px solid rgba(255,255,255,0.10)" zIndex={1} pointerEvents="none" />
      <Box position="absolute" left="28px" top="28px" w="130px" h="2px" bg="#ef2828" zIndex={2} />
      <Box position="absolute" right="28px" bottom="28px" w="130px" h="2px" bg="#ef2828" zIndex={2} />

      <Flex
        position="relative"
        zIndex={2}
        w="100%"
        maxW="1180px"
        px={{ base: 5, md: 10 }}
        py={{ base: 6, md: 10 }}
        align="center"
        justify="space-between"
        gap={8}
        direction={{ base: 'column', lg: 'row' }}
      >
        <VStack align={{ base: 'center', lg: 'flex-start' }} spacing={5} textAlign={{ base: 'center', lg: 'left' }} flex="1">
          <VStack align={{ base: 'center', lg: 'flex-start' }} spacing={4}>
            <Image
              src={FRAGFI_LOGO}
              alt="AlphaOps"
              boxSize={{ base: '132px', md: '168px' }}
              objectFit="contain"
              borderRadius="0"
              border="0"
              boxShadow="0 18px 42px rgba(239, 40, 40, 0.30), 0 0 28px rgba(255,255,255,0.10)"
            />
            <Box>
              <Text color="white" fontWeight="900" letterSpacing="0.34em" fontSize={{ base: '2xl', md: '4xl' }} lineHeight="1">
                ALPHAOPS
              </Text>
              <Text mt={3} color="#ef2828" fontSize="sm" letterSpacing="0.18em" textTransform="uppercase">
                {getLoadingText(state)}...
              </Text>
            </Box>
          </VStack>

          <HStack spacing={3} opacity={0.78} justify={{ base: 'center', lg: 'flex-start' }}>
            {[MACHINEGUN, ROCKET, RIFLE, SHOTGUN].map((weapon) => (
              <Image
                key={weapon}
                src={weapon}
                alt=""
                boxSize={{ base: '34px', md: '42px' }}
                objectFit="contain"
                filter="grayscale(1) brightness(1.6) contrast(1.25) drop-shadow(0 0 10px rgba(239,40,40,0.26))"
              />
            ))}
          </HStack>
        </VStack>

        <Box
          w="100%"
          maxW="430px"
          bg="rgba(5, 6, 8, 0.76)"
          border="1px solid rgba(255,255,255,0.14)"
          borderLeft="4px solid #ef2828"
          boxShadow="0 28px 80px rgba(0,0,0,0.55), inset 0 0 0 1px rgba(239,40,40,0.10)"
          clipPath="polygon(0 0, calc(100% - 22px) 0, 100% 22px, 100% 100%, 22px 100%, 0 calc(100% - 22px))"
          p={{ base: 5, md: 6 }}
        >
          <HStack justify="space-between" mb={4} color="whiteAlpha.650" fontSize="xs" letterSpacing="0.18em" textTransform="uppercase">
            <Text>Match load</Text>
            <Text color="#ff4545">{Math.round(progress)}%</Text>
          </HStack>

          <Progress
            width="100%"
            value={progress}
            h="10px"
            borderRadius="0"
            bg="rgba(255,255,255,0.10)"
            sx={{
              '& > div': {
                background: 'linear-gradient(90deg, #5d0707 0%, #ef2828 70%, #ff8a3d 100%)',
                boxShadow: '0 0 22px rgba(239, 40, 40, 0.75)',
              },
            }}
          />

        </Box>
      </Flex>
    </Flex>
  )
}
